import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import Markdown from "../components/site/Markdown";
import { FALLBACK_IMAGE } from "../lib/images";
import { Reveal, useSEO } from "../components/site/Reveal";
import Newsletter from "../components/site/Newsletter";
import ProverbSpotlight from "../components/site/ProverbSpotlight";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = React.useState(null);
  const [notFound, setNotFound] = React.useState(false);

  React.useEffect(() => {
    api
      .get(`/articles/${slug}`)
      .then((r) => setArticle(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  useSEO(article?.title, article?.excerpt, article?.cover_image);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif text-4xl text-ink mb-4">Article not found</h1>
        <Link to="/blog" className="text-gold font-semibold">Back to Journal</Link>
      </div>
    );
  }

  if (!article) {
    return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-ink-muted">Loading...</div>;
  }

  const date = new Date(article.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  return (
    <article>
      <header className="relative min-h-[52vh] flex items-end grain-overlay">
        <div className="absolute inset-0">
          <img src={article.cover_image || FALLBACK_IMAGE} alt={article.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-night via-night/70 to-night/40" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 pb-14 w-full">
          <nav className="flex items-center gap-2 text-xs text-cream/70 mb-5">
            <Link to="/blog" className="hover:text-gold">Journal</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-gold-soft uppercase tracking-widest">{article.category}</span>
          </nav>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-serif text-4xl sm:text-5xl text-cream leading-tight text-shadow-hero"
          >
            {article.title}
          </motion.h1>
          <p className="mt-5 text-sm text-cream/70">
            {article.author} · {date}
          </p>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-16">
        <Reveal>
          <p className="text-xl font-serif text-forest leading-relaxed border-l-2 border-gold pl-6 mb-10">
            {article.excerpt}
          </p>
        </Reveal>
        <div className="prose-yoruba">
          <Markdown content={article.content} />
        </div>

        <ProverbSpotlight seed={article.slug} />

        {article.tags?.length > 0 && (
          <div className="mt-12 pt-8 border-t border-parchment flex flex-wrap gap-2">
            {article.tags.map((t) => (
              <span key={t} className="text-xs bg-sand text-ink-muted rounded-full px-3 py-1.5 border border-parchment">
                #{t}
              </span>
            ))}
          </div>
        )}

        <Link to="/blog" className="inline-flex items-center gap-2 mt-10 text-forest font-semibold hover:text-gold transition-colors" data-testid="back-to-journal-link">
          <ArrowLeft className="w-4 h-4" /> Back to Journal
        </Link>
      </div>

      <Newsletter />
    </article>
  );
}
